/**
 * Pulls every math span (`$...$`, `$$...$$`, `\(...\)`, `\[...\]`) out of the
 * source and replaces it with an opaque placeholder (`MATH_PLACEHOLDER_0_`,
 * `MATH_PLACEHOLDER_1_`, ...). The original LaTeX is kept in a map keyed by
 * placeholder so the `text` render rule can swap them back for `<MathView>`.
 *
 * Regex branches (order matters — earlier branches win):
 *   ```…``` and `…`       → code: returned verbatim, never extracted
 *   multiline $$…$$       → strips the surrounding newlines from the capture
 *   inline   $$…$$        → single-line block math
 *   \[…\]                 → alternate block delimiter
 *   $…$                   → inline math; Pandoc rule — the opening `$` is not
 *                           followed by whitespace, the closing `$` is not
 *                           preceded by whitespace nor followed by a digit or
 *                           another `$`, and the span never crosses a backtick.
 *                           Keeps currency prose like "costs $5 and $10" and
 *                           "expenses are$500" out of math.
 *   \(…\)                 → alternate inline delimiter
 *
 * The CMS applies the same inline rule when it escapes bare currency dollars —
 * keep the two in sync so an item renders identically in both places.
 */
export const CODE_AND_MATH_REGEX =
	/```[\s\S]*?```|`[^`\n]+`|\$\$\s*\n([\s\S]+?)\n\s*\$\$|\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\$(?!\s)([^$`\n]*[^\s$`])\$(?![\d$])|\\\(([\s\S]+?)\\\)/g;

export type MathEntry = { math: string; block: boolean };

export type ExtractMathResult = {
	cleaned: string;
	mathMap: Map<string, MathEntry>;
};

export const extractMath = (text: string): ExtractMathResult => {
	const mathMap = new Map<string, MathEntry>();
	let counter = 0;
	const cleaned = text.replace(CODE_AND_MATH_REGEX, (match, ...args) => {
		// Code fence or inline code — preserve as-is.
		if (match.startsWith("`")) {
			return match;
		}
		// Groups: 1=multiline $$, 2=inline $$, 3=\[...\], 4=inline $, 5=\(...\)
		const [multiBlock, inlineBlock, bracketBlock, inlineDollar, parenInline] =
			args as Array<string | undefined>;
		const math = (
			multiBlock ??
			inlineBlock ??
			bracketBlock ??
			inlineDollar ??
			parenInline
		)?.trim();
		if (!math) {
			return match;
		}
		const block = Boolean(multiBlock ?? inlineBlock ?? bracketBlock);
		const id = `MATH_PLACEHOLDER_${counter++}_`;
		mathMap.set(id, { math, block });
		return id;
	});
	return { cleaned, mathMap };
};
